'use client';

/**
 * useCheckoutCart Hook
 *
 * Focused hook for cart line operations.
 * Handles adding, removing, and updating quantities.
 */

import { useCallback, useRef } from 'react';
import { snapshot as valtioSnapshot, useSnapshot } from 'valtio';

import { useDebouncedCallback } from '@/hooks/useDebounce';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { CHECKOUT_LINES_SYNC_DEBOUNCE_MS } from '@/lib/checkout/constants';
import {
  checkoutCreate,
  checkoutLinesAdd,
  checkoutLinesDelete,
  checkoutLinesUpdate,
  checkoutRefresh,
  checkoutRestore,
} from '@/lib/checkout/operations';
import { setCheckoutCookie } from '@/lib/checkout/cookies.client';
import { localizeApiErrors } from '@/lib/checkout/errors/localizeApiErrors';
import type {
  ApiError,
  CheckoutData,
  CheckoutLineData,
  CheckoutLineInput,
} from '@/lib/checkout/types';
import { getChannelFromLocale } from '@/lib/configuration/channels';
import { EVENT_NAME } from '@/lib/gtm/const';
import { buildPreOrderItemExtras } from '@/lib/gtm/preOrder';
import {
  resolveAddedCheckoutLines,
  resolveRemovedCheckoutLines,
} from '@/lib/gtm/cartLineResolution';
import { getCountryCodeFromChannel } from '@/lib/const';
import { isTaxIncluded } from '@/lib/tax';
import { resolveCheckoutGtmExtras } from '@/lib/gtm/resolveCheckoutGtmExtras';
import { devError } from '@/lib/utils/devLog';
import { parseOrderAppCtaType, type OrderAppCtaType } from '@/lib/product/cta';
import { gwpProductCheck } from '@/lib/gwp';
import { checkoutActions, checkoutState, type CheckoutLine } from '@/states/checkout';
import { markChannelNeedsRefresh, saveToChannelSnapshot } from '@/states/checkout/channelState';

type CartRollbackSnapshot = {
  lines: CheckoutLine[];
  id: string | null;
  checkout: CheckoutData | null;
};

function captureCartRollbackSnapshot(): CartRollbackSnapshot {
  // Valtio proxies cannot be structuredClone'd — use snapshot / JSON roundtrip instead.
  return {
    lines: valtioSnapshot(checkoutState.lines) as CheckoutLine[],
    id: checkoutState.id,
    checkout: checkoutState.checkout
      ? (valtioSnapshot(checkoutState.checkout) as CheckoutData)
      : null,
  };
}

function restoreCartRollbackSnapshot(snapshot: CartRollbackSnapshot): void {
  checkoutState.lines = snapshot.lines;
  checkoutState.id = snapshot.id;
  checkoutState.checkout = snapshot.checkout;
}

/**
 * Map API errors to simple format
 */
function mapErrors(errors: ApiError[]): Array<{ code: string; message: string; field?: string }> {
  return errors.map((e) => ({
    code: e.code || 'UNKNOWN',
    message: e.message || 'An error occurred',
    field: e.field || undefined,
  }));
}

function resolveCartErrorLocale(locale?: string): string {
  return locale ?? 'us-en';
}

// Valid CTA types for OrderApp — use parseOrderAppCtaType for known values.

/**
 * Map CTA input to valid OrderApp CTA type
 */
function buildCheckoutLineInput(input: AddLineInput): CheckoutLineInput {
  const ctaValue = mapCtaToOrderApp(input.cta);

  return {
    variantId: input.variantId,
    quantity: input.quantity || 1,
    metadata: [{ key: 'item.cta_selected', value: ctaValue }, ...(input.metadata || [])],
    ...(input.watchLineId && { watchLineId: input.watchLineId }),
    ...(input.strapRelatedProducts && {
      strapRelatedProducts: { buckleId: input.strapRelatedProducts.buckleId },
    }),
  };
}

function mapCtaToOrderApp(cta?: string): OrderAppCtaType {
  const parsed = parseOrderAppCtaType(cta);
  if (parsed) {
    return parsed;
  }

  if (!cta) return 'ORDER';

  const upperCta = cta.toUpperCase().replaceAll('-', '_');

  const ctaMap: Record<string, OrderAppCtaType> = {
    ADD_TO_BAG: 'ORDER',
    ADD_TO_CART: 'ORDER',
    BUY: 'ORDER',
    PREORDER: 'PRE_ORDER',
    PRE_ORDER_NOW: 'PRE_ORDER',
    NOTIFY_ME: 'NOTIFY',
  };

  return ctaMap[upperCta] || 'ORDER';
}

/**
 * Attach checkout to the logged-in user's account (fire-and-forget)
 *
 * This ensures the checkout persists to the user's account so they can
 * continue shopping on another device or after logging out/in.
 *
 * Non-blocking: failures don't affect the cart operation.
 */
async function attachCheckoutToCustomer(checkoutId: string): Promise<void> {
  try {
    const response = await fetch('/api/checkout/attach-customer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkoutId }),
    });

    await response.json();
  } catch (error) {
    // Don't fail the cart operation if attach fails
    devError('[Cart] Failed to attach checkout to customer (non-fatal):', error);
  }
}

/**
 * Line item input for adding to cart
 */
export interface AddLineInput {
  variantId: string;
  sku?: string;
  /** Product name (e.g., "Navitimer B01 Chronograph 43") */
  name?: string;
  /** Variant descriptor for optimistic display (e.g., "Stainless steel - Black")
   * Falls back to material if not provided. Used for better UX during optimistic update. */
  variantName?: string;
  /** Material (e.g., "Stainless steel") - used as fallback for variantName */
  material?: string;
  price?: number;
  currency?: string;
  quantity?: number;
  image?: string;
  productType?: string;
  cta?: string;
  metadata?: Array<{ key: string; value: string }>;
  /** Watch line ID - required for warranty extension products */
  watchLineId?: string;
  /** Strap related products - for strap products */
  strapRelatedProducts?: {
    buckleId: string;
  };
  /** Warranty extension duration in years (e.g., "2" or "3") - for optimistic cart display */
  warrantyExtensionDuration?: string;
  /** GTM item_list_name for add_to_cart (PLP, PDP, wishlist, search) */
  itemListName?: string;
}

/**
 * Cart operations result
 */
export interface CartOperationResult {
  success: boolean;
  errors: Array<{ code: string; message: string; field?: string }>;
}

/**
 * Hook options
 */
export interface UseCheckoutCartOptions {
  locale?: string;
}

/**
 * Focused hook for cart line operations
 */
export function useCheckoutCart(options: UseCheckoutCartOptions = {}) {
  const snapshot = useSnapshot(checkoutState);
  const { sendEcommerceFromCheckoutLines } = useGoogleAnalytics();
  const pendingLineUpdatesRef = useRef<Map<string, number>>(new Map());
  const rollbackSnapshotRef = useRef<{
    lines: typeof checkoutState.lines;
    checkout: typeof checkoutState.checkout;
  } | null>(null);
  const flushResolversRef = useRef<Array<(result: CartOperationResult) => void>>([]);

  // Coalesce rapid quantity clicks into one checkoutLinesUpdate. Optimistic UI updates
  // immediately; totalPrice only changes after the debounced sync completes.
  // Parse locale
  const locale = options.locale || 'us-en';
  const [, languageSlug = 'en'] = locale.split('-');
  const channelSlug = getChannelFromLocale(locale);

  const resolvePendingFlush = useCallback((result: CartOperationResult) => {
    const resolvers = flushResolversRef.current;
    flushResolversRef.current = [];
    resolvers.forEach((resolve) => resolve(result));
  }, []);

  const trackAddToCart = useCallback(
    (checkout: CheckoutData | null | undefined, input: AddLineInput) => {
      const addedLines = resolveAddedCheckoutLines(checkout, input);
      if (!addedLines.length) {
        return;
      }

      const cta = input.cta?.toUpperCase().replaceAll('-', '_');
      const countryCode = getCountryCodeFromChannel(checkout?.channel?.slug ?? channelSlug);
      const showTax = isTaxIncluded(countryCode);
      const checkoutExtras = resolveCheckoutGtmExtras(checkout, showTax);
      const itemExtras =
        cta === 'PRE_ORDER'
          ? buildPreOrderItemExtras({
              ctaType: 'PRE_ORDER',
              productType: input.productType,
              itemListName: input.itemListName,
            })
          : undefined;
      const eventExtras = checkoutExtras ? { ...itemExtras, ...checkoutExtras } : itemExtras;

      void sendEcommerceFromCheckoutLines({
        event: EVENT_NAME.ADD_TO_CART,
        lines: addedLines,
        eventExtras,
      });
    },
    [channelSlug, sendEcommerceFromCheckoutLines]
  );

  const trackRemoveFromCart = useCallback(
    (deletedLines: CheckoutLineData[], checkoutBeforeRemoval: CheckoutData | null | undefined) => {
      if (!deletedLines.length) {
        return;
      }

      const checkout = checkoutBeforeRemoval ?? checkoutState.checkout;
      const countryCode = getCountryCodeFromChannel(checkout?.channel?.slug ?? channelSlug);
      const showTax = isTaxIncluded(countryCode);

      void sendEcommerceFromCheckoutLines({
        event: EVENT_NAME.REMOVE_FROM_CART,
        lines: deletedLines,
        eventExtras: resolveCheckoutGtmExtras(checkout, showTax),
      });
    },
    [channelSlug, sendEcommerceFromCheckoutLines]
  );

  const flushPendingLineUpdates = useCallback(async () => {
    const checkoutId = checkoutState.id;
    if (!checkoutId) {
      pendingLineUpdatesRef.current.clear();
      rollbackSnapshotRef.current = null;
      resolvePendingFlush({ success: true, errors: [] });
      return;
    }

    const lines = Array.from(pendingLineUpdatesRef.current.entries()).map(([lineId, quantity]) => ({
      lineId,
      quantity,
    }));

    if (lines.length === 0) {
      resolvePendingFlush({ success: true, errors: [] });
      return;
    }

    pendingLineUpdatesRef.current.clear();
    const rollbackSnapshot = rollbackSnapshotRef.current;
    rollbackSnapshotRef.current = null;

    checkoutActions.bumpMutationVersion();
    checkoutActions.setProcessing(true);
    checkoutActions.clearErrors();

    try {
      const { checkout, errors } = await checkoutLinesUpdate({
        id: checkoutId,
        lines,
        languageCode: languageSlug.toUpperCase(),
      });

      if (errors?.length) {
        if (rollbackSnapshot) {
          checkoutState.lines = rollbackSnapshot.lines;
          checkoutState.checkout = rollbackSnapshot.checkout;
        }
        checkoutActions.setErrors(errors, 'checkout');
        resolvePendingFlush({ success: false, errors: mapErrors(errors) });
        return;
      }

      if (checkout) {
        checkoutActions.updateCheckout(checkout);
      }

      resolvePendingFlush({ success: true, errors: [] });
    } catch {
      if (rollbackSnapshot) {
        checkoutState.lines = rollbackSnapshot.lines;
        checkoutState.checkout = rollbackSnapshot.checkout;
      }
      resolvePendingFlush({
        success: false,
        errors: [{ code: 'UNKNOWN', message: 'Failed to update cart' }],
      });
    } finally {
      checkoutActions.setProcessing(false);
    }
  }, [languageSlug, resolvePendingFlush]);

  const debouncedFlushPendingLineUpdates = useDebouncedCallback(
    flushPendingLineUpdates,
    CHECKOUT_LINES_SYNC_DEBOUNCE_MS
  );

  /**
   * Add a line to the cart
   *
   * OPTIMISTIC UPDATE STRATEGY:
   * 1. Add line to local state immediately (instant UI feedback)
   * 2. Sync with backend in background
   * 3. Roll back on error
   *
   * Returns immediately after optimistic update for instant UX.
   * Server sync happens in background - errors trigger rollback + notification.
   */
  const addLine = useCallback(
    async (input: AddLineInput): Promise<CartOperationResult> => {
      const rollbackSnapshot = captureCartRollbackSnapshot();

      // OPTIMISTIC UPDATE - happens immediately for all flows
      // Store product name and variant descriptor separately for proper display:
      // - name: Product name (e.g., "Super Chronomat B01 44")
      // - subtitle: Variant descriptor (e.g., "18k red gold - Brown")
      const productName = input.name || 'Product';

      // Clean variant subtitle - remove " | ID" suffix if present from Algolia/Saleor data
      // e.g., "18k red gold | 18KREG" → "18k red gold"
      const cleanValue = (val: string | undefined) => {
        if (!val) return '';
        const pipeIndex = val.indexOf('|');
        return pipeIndex !== -1 ? val.substring(0, pipeIndex).trim() : val.trim();
      };
      const variantSubtitle = cleanValue(input.variantName) || cleanValue(input.material) || '';

      checkoutActions.addLine({
        variantId: input.variantId,
        sku: input.sku || '',
        name: productName,
        subtitle: variantSubtitle,
        price: input.price || 0,
        currency: input.currency || 'USD',
        quantity: input.quantity || 1,
        image: input.image,
        productType: input.productType,
        isOptimistic: true,
        // Warranty extension duration for optimistic cart upsell display
        warrantyExtensionDuration: input.warrantyExtensionDuration,
      });

      // Mark as syncing (not processing - syncing is background, processing blocks UI)
      checkoutActions.bumpMutationVersion();
      checkoutActions.setSyncing(true);
      checkoutActions.clearErrors();

      // Try to restore existing checkout
      const storedId = checkoutState.id;

      if (storedId) {
        try {
          await checkoutRestore({
            id: storedId,
            languageCode: languageSlug.toUpperCase(),
          });
        } catch {
          // Continue - will use state.id if available
        }
      }

      const existingCheckoutId = checkoutState.id;

      if (!existingCheckoutId) {
        // Create checkout with line - optimistic update already done above
        try {
          const { checkout, errors } = await checkoutCreate({
            channelSlug,
            languageCode: languageSlug.toUpperCase(),
            lines: [buildCheckoutLineInput(input)],
          });

          if (errors?.length) {
            restoreCartRollbackSnapshot(rollbackSnapshot);
            const localizedErrors = localizeApiErrors(
              errors,
              resolveCartErrorLocale(options?.locale)
            );
            checkoutActions.setErrors(localizedErrors, 'checkout');
            return { success: false, errors: mapErrors(localizedErrors) };
          }

          if (checkout) {
            checkoutActions.updateCheckout(checkout);
            checkoutActions.setChannel(channelSlug);
            // Set checkout cookie for persistence
            setCheckoutCookie(channelSlug, checkout.id);
            // Save to localStorage cache so cart survives page refresh
            saveToChannelSnapshot(channelSlug, {
              id: checkout.id,
              checkout: checkout,
              email: checkout.email || null,
              deliveryMethodId: checkout.deliveryMethod?.id || null,
            });
            // Attach to logged-in user (fire-and-forget) - only if not already attached
            if (!checkout.user) {
              attachCheckoutToCustomer(checkout.id);
            }
          }
          trackAddToCart(checkout, input);
          return { success: true, errors: [] };
        } catch (error) {
          restoreCartRollbackSnapshot(rollbackSnapshot);
          throw error;
        } finally {
          checkoutActions.setSyncing(false);
        }
      }

      // Add line to existing checkout - optimistic update already done above
      try {
        // Sync with backend
        const lineInput = buildCheckoutLineInput(input);

        // Legacy gwpProductCheck: clear any prior gift selection before bag changes
        // so complimentary gifts cannot stack when the cart is mutated.
        await gwpProductCheck();

        const { checkout, errors } = await checkoutLinesAdd({
          id: existingCheckoutId,
          lines: [lineInput],
          languageCode: languageSlug.toUpperCase(),
          channelSlug,
        });

        if (errors?.length) {
          restoreCartRollbackSnapshot(rollbackSnapshot);
          const localizedErrors = localizeApiErrors(
            errors,
            resolveCartErrorLocale(options?.locale)
          );
          checkoutActions.setErrors(localizedErrors, 'checkout');
          return { success: false, errors: mapErrors(localizedErrors) };
        }

        if (checkout) {
          checkoutActions.updateCheckout(checkout);
          // Save to localStorage cache so cart survives page refresh
          saveToChannelSnapshot(channelSlug, {
            id: checkout.id,
            checkout: checkout,
            email: checkout.email || null,
            deliveryMethodId: checkout.deliveryMethod?.id || null,
          });
          // Ensure checkout is attached to logged-in user (fire-and-forget)
          // Only if not already attached - handles cases where checkout was created before login
          if (!checkout.user) {
            attachCheckoutToCustomer(existingCheckoutId);
          }
        }

        trackAddToCart(checkout, input);
        return { success: true, errors: [] };
      } catch (error) {
        restoreCartRollbackSnapshot(rollbackSnapshot);
        throw error;
      } finally {
        checkoutActions.setSyncing(false);
      }
    },
    [channelSlug, languageSlug, options?.locale, trackAddToCart]
  );
  const removeLine = useCallback(
    async (lineId: string): Promise<CartOperationResult> => {
      // Guard against concurrent removals — the disabled button relies on a
      // React re-render that may not have flushed yet, so check the raw proxy.
      if (checkoutState.isProcessing) {
        return { success: false, errors: [{ code: 'BUSY', message: 'Cart is updating' }] };
      }

      checkoutActions.bumpMutationVersion();
      checkoutActions.setProcessing(true);
      checkoutActions.setSyncing(true);
      checkoutActions.clearErrors();

      // Store previous state for rollback on failure
      const previousLines = [...checkoutState.lines];
      const previousCheckout = checkoutState.checkout ? { ...checkoutState.checkout } : null;
      const deletedLines = resolveRemovedCheckoutLines(previousCheckout?.lines, lineId);

      const completeRemoveSuccess = (): CartOperationResult => {
        checkoutActions.clearPendingRemoval(lineId);
        trackRemoveFromCart(deletedLines, previousCheckout);
        return { success: true, errors: [] };
      };

      try {
        // Optimistic update (this also adds lineId to pendingRemovals)
        checkoutActions.removeLine(lineId);

        if (checkoutState.id) {
          // Legacy gwpProductCheck: clear any prior gift selection before bag changes.
          await gwpProductCheck();

          const { checkout, errors, deleteSucceeded } = await checkoutLinesDelete({
            id: checkoutState.id,
            linesIds: [lineId],
            languageCode: languageSlug.toUpperCase(),
          });

          // Handle "line not found" error — sync with server to fix stale state.
          // Only match when the error message explicitly mentions line IDs;
          // a bare generic_exception without a line-related message is a real server error.
          const isLineNotFound = errors?.some((e) => {
            const msg = e.message?.toLowerCase() || '';
            const code = e.code?.toLowerCase() || '';
            const isLineRelatedMessage =
              msg.includes('part of checkout') ||
              msg.includes('line_id') ||
              msg.includes('line not found');
            return isLineRelatedMessage || code === 'line_not_found';
          });

          if (isLineNotFound) {
            // The line doesn't exist on server - sync from server to get correct state
            const refreshResult = await checkoutRefresh({
              id: checkoutState.id,
              languageCode: languageSlug.toUpperCase(),
            });
            if (refreshResult.checkout) {
              checkoutActions.updateCheckout(refreshResult.checkout);
              // Also update localStorage cache so refresh doesn't restore stale data
              saveToChannelSnapshot(channelSlug, {
                id: refreshResult.checkout.id,
                checkout: refreshResult.checkout,
                email: refreshResult.checkout.email || null,
                deliveryMethodId: refreshResult.checkout.deliveryMethod?.id || null,
              });
            }
            // If refresh failed, keep the optimistic update (line removed)
            return completeRemoveSuccess();
          }

          // Only rollback if the server-side delete actually failed
          // If deleteSucceeded is true, don't rollback even if refresh failed
          if (errors?.length && !deleteSucceeded) {
            // Rollback on error
            checkoutState.lines = previousLines;
            if (previousCheckout) {
              checkoutState.checkout = previousCheckout;
            }
            checkoutActions.clearPendingRemoval(lineId);
            checkoutActions.setErrors(errors, 'checkout');
            return { success: false, errors: mapErrors(errors) };
          }

          if (checkout) {
            checkoutActions.updateCheckout(checkout);
            // Update localStorage cache so refresh shows correct data
            saveToChannelSnapshot(channelSlug, {
              id: checkout.id,
              checkout: checkout,
              email: checkout.email || null,
              deliveryMethodId: checkout.deliveryMethod?.id || null,
            });
          } else if (deleteSucceeded) {
            // Delete succeeded but refresh failed — mark cache stale so next
            // access fetches fresh data instead of serving the old snapshot.
            markChannelNeedsRefresh(channelSlug);
          }
          // If deleteSucceeded but no checkout (refresh failed), the optimistic update stays
          // This is correct - the item IS deleted on the server
        }

        return completeRemoveSuccess();
      } catch (error) {
        // Rollback on exception
        checkoutState.lines = previousLines;
        if (previousCheckout) {
          checkoutState.checkout = previousCheckout;
        }
        // Clear pending removal on error so rollback shows the item
        checkoutActions.clearPendingRemoval(lineId);
        throw error;
      } finally {
        checkoutActions.setProcessing(false);
        checkoutActions.setSyncing(false);
      }
    },
    [channelSlug, languageSlug, trackRemoveFromCart]
  );
  const updateQuantity = useCallback(
    (lineId: string, quantity: number): Promise<CartOperationResult> => {
      if (pendingLineUpdatesRef.current.size === 0) {
        rollbackSnapshotRef.current = {
          lines: [...checkoutState.lines],
          checkout: checkoutState.checkout ? { ...checkoutState.checkout } : null,
        };
      }

      checkoutActions.updateQuantity(lineId, quantity);

      if (!checkoutState.id) {
        return Promise.resolve({ success: true, errors: [] });
      }

      pendingLineUpdatesRef.current.set(lineId, quantity);

      return new Promise<CartOperationResult>((resolve) => {
        flushResolversRef.current.push(resolve);
        debouncedFlushPendingLineUpdates();
      });
    },
    [debouncedFlushPendingLineUpdates]
  );

  /**
   * Clear all lines from cart
   */
  const clearCart = useCallback(() => {
    checkoutActions.clear();
  }, []);

  return {
    // State
    lines: snapshot.lines,
    linesCount: snapshot.linesCount,
    isEmpty: snapshot.isEmpty,
    isProcessing: snapshot.isProcessing,
    subtotal: snapshot.subtotal,
    currency: snapshot.currency,

    // Operations
    addLine,
    removeLine,
    updateQuantity,
    clearCart,
  };
}
